import { useQuery } from '@tanstack/react-query';
import { api, endpoints } from '@/api/client';
import { queryKeys } from '@/api/queryClient';

export interface FogHealthResponse {
  status: string;
  fog_server: string;
  connected: boolean;
  error?: string;
}

export interface FogTask {
  id: number;
  name: string;
  hostID: number;
  hostName?: string;
  stateID: number;
  percent?: number;
  typeID: number;
  typeName?: string;
}

export interface FogDashboardResponse {
  connected: boolean;
  hosts: {
    total: number;
    pending: number;
  };
  groups: {
    total: number;
  };
  images: {
    total: number;
  };
  tasks: {
    active: number;
    queued: number;
    recent: FogTask[];
  };
  server: {
    version: string;
    kernel?: string;
    hostname?: string;
    uptime?: string;
  } | null;
  error?: string;
}

/**
 * Hook to check FOG server health status
 * Polls every 30 seconds to keep status up-to-date
 */
export function useFogStatus() {
  return useQuery({
    queryKey: queryKeys.fog.health,
    queryFn: async () => {
      try {
        const response = await api.get<FogHealthResponse>(endpoints.fog.health);
        return response;
      } catch (error) {
        // Return disconnected status on error
        return {
          status: 'error',
          fog_server: '',
          connected: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    // Refetch every 30 seconds
    refetchInterval: 30000,
    // Don't retry on error - we handle it gracefully
    retry: false,
    // Keep previous data while refetching
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook to fetch FOG dashboard statistics
 * Polls every 15 seconds to keep stats up-to-date
 */
export function useFogDashboard() {
  return useQuery({
    queryKey: queryKeys.fog.dashboard,
    queryFn: async () => {
      // Longer timeout for dashboard - it makes multiple relay calls
      const response = await api.get<FogDashboardResponse>(endpoints.fog.dashboard, {
        timeout: 120000, // 2 minutes timeout for relay operations
      });
      return response;
    },
    // Refetch every 60 seconds (longer interval due to slow relay)
    refetchInterval: 60000,
    // Retry once on error
    retry: 1,
    // Keep previous data while refetching
    placeholderData: (previousData) => previousData,
  });
}
